import React, { useState, useEffect } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';

const ArangoDownloadPage = () => {
  const [selectedVersion, setSelectedVersion] = useState('3.12.5-2');
  const [selectedTab, setSelectedTab] = useState('linux');
  const [selectedDistro, setSelectedDistro] = useState('ubuntu');
  const [copiedCode, setCopiedCode] = useState('');
  const [releasesData, setReleasesData] = useState(null);
  const [systemRequirements, setSystemRequirements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data on component mount
  useEffect(() => {
    fetchReleasesData();
    fetchSystemRequirements();
  }, []);

  const fetchReleasesData = async () => {
    try {
      console.log('Starting fetchReleasesData...');
      setLoading(true);
      setError(null);
  
      // Use local JSON file
      console.log('Fetching ./arango-releases.json...');
      const response = await fetch('./arango-releases.json');
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Loaded local releases data:', data);
      console.log('Releases:', data.releases);
      console.log('Setting loading to false...');
      
      setReleasesData(data);
      // Use the latest version from the JSON
      const latestVersion = data.latest?.enterprise || "3.12.5-2";
      setSelectedVersion(latestVersion);
      setLoading(false);
      console.log('Loading set to false');
      } catch (err) {
        console.error('Error loading releases data:', err);
        console.log('Using fallback data...');
        // Fallback to hardcoded data if fetch fails
        const fallbackData = {
          "latest": {
            "enterprise": "3.12.5-2"
          },
          "releases": {
            "3.12.5-2": {
              "edition": "enterprise",
              "status": "active",
              "eol_date": null,
              "artifacts": {
                "debian-enterprise-amd64": {
                  "ARANGODB_VERSION": "3.12.5-2",
                  "DEBIAN_NAME_SERVER": "arangodb3e_3.12.5.2-1_amd64.deb",
                  "DEBIAN_NAME_CLIENT": "arangodb3e-client_3.12.5.2-1_amd64.deb",
                  "DEBIAN_SHA256_SERVER": "3b00700e7ff7896bd7e71d1fbaf95bf7b7e6a38c7974330cd2620893f004baec",
                  "DEBIAN_SHA256_CLIENT": "e972387f280efd23fcc99779ae2d3e2be16d90794dfd9009215ca0272f23415f",
                  "DEBIAN_SIZE_SERVER": "233",
                  "DEBIAN_SIZE_CLIENT": "60",
                  "DEBIAN_VERSION": "3.12.5.2-1",
                  "DOWNLOAD_LINK": "9c169fe900ff79790395784287bfa82f0dc0059375a34a2881b9b745c8efd42e"
                },
                "docker-enterprise": {
                  "DOCKER_IMAGE": "arangodb/enterprise:3.12.5.2"
                },
                "k8s-enterprise": {
                  "DOCKER_IMAGE": "arangodb/enterprise:3.12.5.2"
                }
              }
            }
          }
        };
        console.log('Using fallback data');
        setReleasesData(fallbackData);
        setSelectedVersion("3.12.5-2");
        setLoading(false);
        console.log('Fallback loading set to false');
      }
  };

  const fetchSystemRequirements = async () => {
    try {
        const response = await fetch('./system-requirements.json');
        if (!response.ok) {
          console.warn('System requirements file not found, using fallback data');
          throw new Error('Failed to fetch system requirements data');
        }
        const data = await response.json();
        console.log('Loaded system requirements:', data);
        setSystemRequirements(data);
      } catch (err) {
        console.warn('Using fallback system requirements data:', err);
      // Fallback to hardcoded data if fetch fails
      const fallbackData = {
        distributions: {
          ubuntu: {
            name: "Ubuntu / Debian",
            supported_versions: "20.04 – 24.04",
            architectures: "x86-64, ARM64"
          },
          redhat: {
            name: "RedHat / CentOS / Fedora",
            supported_versions: "RHEL 8–9 / CentOS 7–9 / Fedora ≥ 36",
            architectures: "x86-64, ARM64"
          },
          suse: {
            name: "openSUSE / SLE",
            supported_versions: "openSUSE 15.x / SLE 15.x",
            architectures: "x86-64, ARM64"
          },
          tarball: {
            name: "TAR (manual install)",
            supported_versions: "Any Linux, no root required",
            architectures: "x86-64, ARM64",
            requirements: "glibc ≥ 2.31, OpenSSL ≥ 1.1"
          }
        }
      };
      setSystemRequirements(fallbackData);
    }
  };

  // Generate versions from releases data
  const getVersions = () => {
    if (!releasesData || !releasesData.releases) return [];
    
    const versions = [];
    
    if (Array.isArray(releasesData.releases)) {
      // Array structure: iterate through array
      releasesData.releases.forEach(release => {
        const version = release.version;
        const isLatest = releasesData.latest && releasesData.latest.enterprise === version;
        const status = release.status || 'active';
        
        versions.push({
          value: version,
          label: isLatest ? `Latest (${version})` : version,
          exact: version,
          status: status,
          edition: release.edition
        });
      });
    } else {
      // Object structure: iterate through keys
      Object.keys(releasesData.releases).forEach(version => {
        const release = releasesData.releases[version];
        const isLatest = releasesData.latest && releasesData.latest.enterprise === version;
        const status = release.status || 'active';
        
        versions.push({
          value: version,
          label: isLatest ? `Latest (${version})` : version,
          exact: version,
          status: status,
          edition: release.edition
        });
      });
    }
    
    // Sort versions (latest first)
    return versions.sort((a, b) => {
      if (a.status === 'active' && b.status !== 'active') return -1;
      if (a.status !== 'active' && b.status === 'active') return 1;
      return b.value.localeCompare(a.value);
    });
  };

  const tabs = [
    { id: 'linux', label: 'Linux Packages' },
    { id: 'docker', label: 'Docker' },
    { id: 'kubernetes', label: 'Kubernetes (Helm)' },
    { id: 'nightly', label: 'Nightly' }
  ];

  const distributions = [
    { id: 'ubuntu', label: 'Ubuntu / Debian' },
    { id: 'redhat', label: 'RedHat / CentOS / Fedora' },
    { id: 'suse', label: 'openSUSE / SLE' },
    { id: 'tarball', label: 'TAR' }
  ];


  const handleCopy = (text, id) => {
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(''), 2000);
  };

  // Helper function to get current release data
  const getCurrentRelease = () => {
    console.log('getCurrentRelease called with:', { releasesData, selectedVersion });
    if (!releasesData || !releasesData.releases) {
      console.log('No releases data available');
      return null;
    }
    // Handle both array and object structures
    let release;
    if (Array.isArray(releasesData.releases)) {
      // Array structure: find by version property
      release = releasesData.releases.find(r => r.version === selectedVersion);
    } else {
      // Object structure: get by version key
      release = releasesData.releases[selectedVersion];
    }
    console.log('Found release:', release);
    return release;
  };

  // Helper function to get artifact data for current selection
  const getArtifactData = (artifactType) => {
    const release = getCurrentRelease();
    if (!release || !release.artifacts) {
      console.log('No release or artifacts found:', { release, artifacts: release?.artifacts });
      return null;
    }
    
    // For docker and k8s, return single artifact
    if (artifactType === 'docker') {
      return release.artifacts['docker-enterprise'] || null;
    } else if (artifactType === 'k8s') {
      return release.artifacts['k8s-enterprise'] || null;
    }
    
    // For Linux packages, return both architectures
    const artifacts = {};
    
    if (artifactType === 'debian') {
      artifacts.x86_64 = release.artifacts['debian-enterprise-amd64'] || null;
      artifacts.arm64 = release.artifacts['debian-enterprise-arm64'] || null;
    } else if (artifactType === 'rhel' || artifactType === 'suse') {
      artifacts.x86_64 = release.artifacts['rpm-enterprise-x86_64'] || null;
      artifacts.arm64 = release.artifacts['rpm-enterprise-aarch64'] || null;
    } else if (artifactType === 'tarball') {
      artifacts.x86_64 = release.artifacts['tgz-enterprise-x86_64'] || null;
      artifacts.arm64 = release.artifacts['tgz-enterprise-arm64'] || null;
    }
    
    console.log('Found artifacts for', artifactType, ':', artifacts);
    return artifacts;
  };

  // Helper function to format file size
  const formatFileSize = (sizeInMB) => {
    if (!sizeInMB) return 'Unknown';
    return `${sizeInMB} MB`;
  };

  // Helper function to get download URL
  const getDownloadUrl = (artifactData, fileName) => {
    if (!artifactData || !artifactData.DOWNLOAD_LINK) return '#';
    const baseUrl = 'https://download.arangodb.com';
    
    // Use the DOWNLOAD_LINK with the correct path structure
    const downloadLink = artifactData.DOWNLOAD_LINK.startsWith('/') 
      ? artifactData.DOWNLOAD_LINK.substring(1) 
      : artifactData.DOWNLOAD_LINK;
    
    // All files use the same path structure: /{DOWNLOAD_LINK}/arangodb312/Enterprise/Linux/{fileName}
    return `${baseUrl}/${downloadLink}/arangodb312/Enterprise/Linux/${fileName}`;
  };

  // Helper: method-specific notes from JSON with safe fallbacks
  const getMethodNotes = (method) => {
    const jsonNotes = releasesData?.ui?.notes?.[method]?.snippets;
    if (Array.isArray(jsonNotes) && jsonNotes.length > 0) {
      return jsonNotes;
    }
    switch (method) {
      case 'linux':
        return [
          'Service: systemd service arangodb3 (enable/start via systemctl).',
          'Access: Web UI on http://localhost:8529.',
          'Auth: Initial user is root; set a strong password on first use.',
          'Verify: Use SHA256 only for direct file downloads.'
        ];
      case 'docker':
        return [
          'Auth required: set -e ARANGO_ROOT_PASSWORD=<password>.',
          'Access: map -p 8529:8529, then open http://localhost:8529.',
          'Persistence: mount a volume at /var/lib/arangodb3.',
          'No systemd: the container runs the process directly.'
        ];
      case 'kubernetes':
        return [
          'Credentials: set the root password in Helm values or a Kubernetes Secret.',
          'Access: expose via Service/Ingress to reach the Web UI.',
          'Lifecycle: managed by Helm/Operator, not systemd.'
        ];
      case 'tarball':
        return [
          'Start manually: run ./bin/arangod (or create your own service).',
          'Access: Web UI on http://localhost:8529 after start.',
          'Verify: check the tarball SHA256 before extracting.'
        ];
      default:
        return [];
    }
  };

  const MethodNotes = ({ method }) => {
    const notes = getMethodNotes(method);
    if (!notes || notes.length === 0) return null;
    return (
      <ul className="list-disc pl-5 text-sm text-gray-700 mb-4">
        {notes.map((note, idx) => (
          <li key={idx}>{note}</li>
        ))}
      </ul>
    );
  };


  const CodeBlock = ({ children, id, language = 'bash' }) => (
    <div className="relative group">
      <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm whitespace-pre-wrap break-words">
        <code className={`language-${language}`}>{children}</code>
      </pre>
      <CopyToClipboard text={children} onCopy={() => handleCopy(children, id)}>
        <button className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity">
          {copiedCode === id ? '✓ Copied!' : 'Copy'}
        </button>
      </CopyToClipboard>
    </div>
  );

  const DownloadTable = ({ files }) => {
    // Split files by architecture
    const x86Files = files.filter(file => file.arch === 'x86-64');
    const armFiles = files.filter(file => file.arch === 'ARM64');
    
    const renderTable = (archFiles, title) => (
      <div className="mb-6">
        <h6 className="font-medium mb-3 text-gray-700">{title}</h6>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-transparent download-table">
            <thead>
              <tr>
                <th className="border border-gray-300 px-1 py-0.5 text-left bg-[#044926] text-white font-semibold text-xs">Type</th>
                <th className="border border-gray-300 px-1 py-0.5 text-left bg-[#044926] text-white font-semibold text-xs">Arch</th>
                <th className="border border-gray-300 px-1 py-0.5 text-left bg-[#044926] text-white font-semibold text-xs">File</th>
                <th className="border border-gray-300 px-1 py-0.5 text-left bg-[#044926] text-white font-semibold text-xs">Size</th>
                <th className="border border-gray-300 px-1 py-0.5 text-left bg-[#044926] text-white font-semibold text-xs">Download</th>
              </tr>
            </thead>
            <tbody>
              {archFiles.map((file, index) => (
                <tr key={index} className="hover:bg-[rgba(185,255,56,0.1)]">
                  <td className="border border-gray-300 px-4 py-2" data-label="Type">
                    <span className="text-sm font-medium text-gray-800">
                      {file.name.includes('client') ? 'Client Tools' : file.name.includes('debug') || file.name.includes('dbg') ? 'Debug Symbols' : 'Server'}
                    </span>
                  </td>
                  <td className="border border-gray-300 px-4 py-2" data-label="Arch">
                    <span className="text-sm font-medium text-gray-700">
                      {file.arch}
                    </span>
                  </td>
                  <td className="border border-gray-300 px-4 py-2" data-label="File">
                    <div>
                      <div className="font-mono text-sm font-bold">{file.name}</div>
                      <div className="group relative mt-1">
                        <CopyToClipboard 
                          text={file.sha256Full || file.sha256}
                          onCopy={() => setCopiedCode(`sha256-${index}`)}
                        >
                        <span className="font-mono text-xs text-gray-500 cursor-pointer hover:text-blue-600 checksum" title="Click to copy full SHA256">
                          SHA256: {file.sha256Full || file.sha256}
                        </span>
                        </CopyToClipboard>
                        <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                          {copiedCode === `sha256-${index}` ? 'Copied!' : 'Click to copy full hash'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="border border-gray-300 px-4 py-2" data-label="Size">{file.size}</td>
                  <td className="border border-gray-300 px-4 py-2" data-label="Download">
                    <a 
                      href={file.url} 
                      className="inline-flex items-center px-3 py-1.5 bg-[#BEFE99] text-black text-sm font-medium rounded-md hover:bg-[#58DF20] transition-colors duration-200"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );

    return (
      <div>
        {x86Files.length > 0 && renderTable(x86Files, 'x86-64 Architecture')}
        {armFiles.length > 0 && renderTable(armFiles, 'ARM64 Architecture')}
      </div>
    );
  };

  const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    
    return (
      <div className="border border-gray-200 rounded-lg">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 flex justify-between items-center"
        >
          <span className="font-medium">{title}</span>
          <span className="text-gray-500">{isOpen ? '−' : '+'}</span>
        </button>
        {isOpen && (
          <div className="p-4 border-t border-gray-200">
            {children}
          </div>
        )}
      </div>
    );
  };

  const renderLinuxTab = () => (
    <div className="space-y-6">
      <div className="pt-4">
        <h3 className="text-lg font-semibold mb-4">Choose Distribution</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 distribution-grid">
          {distributions.map((distro) => (
            <button
              key={distro.id}
              onClick={() => setSelectedDistro(distro.id)}
              className={`flex flex-col items-center gap-2 p-3 rounded-[10px] transition-all duration-200 distribution-button ${
                selectedDistro === distro.id
                  ? 'bg-[#F4FAF4] text-black border-2 border-[#58df20]'
                  : 'bg-[#F4FAF4] text-black border-2 border-[#F4FAF4] hover:bg-[#E8F5E8]'
              }`}
            >
              {distro.id === 'ubuntu' && (
                <div className="flex items-center gap-1">
                  <img src="./images/ubuntu.svg" alt="Ubuntu" className="w-5 h-5" />
                  <img src="./images/debian.svg" alt="Debian" className="w-5 h-5" />
                </div>
              )}
              {distro.id === 'redhat' && (
                <div className="flex items-center gap-1">
                  <img src="./images/redhat.svg" alt="RedHat" className="w-5 h-5" />
                  <img src="./images/centos.svg" alt="CentOS" className="w-5 h-5" />
                  <img src="./images/fedora.svg" alt="Fedora" className="w-5 h-5" />
                </div>
              )}
              {distro.id === 'suse' && (
                <img src="./images/suse.svg" alt="SUSE" className="w-6 h-6" />
              )}
              {distro.id === 'tarball' && (
                <img src="./images/tar.svg" alt="TAR" className="w-6 h-6" />
              )}
              <div className="font-bold text-sm text-center">{distro.label}</div>
            </button>
          ))}
        </div>
      </div>


      {selectedDistro === 'ubuntu' && (() => {
        const artifacts = getArtifactData('debian');
        if (!artifacts) return <div>Loading...</div>;
        
        const files = [];
        
        // Add x86-64 files
        if (artifacts.x86_64?.DEBIAN_NAME_SERVER) {
          files.push({
            name: artifacts.x86_64.DEBIAN_NAME_SERVER,
            arch: 'x86-64',
            size: formatFileSize(artifacts.x86_64.DEBIAN_SIZE_SERVER),
            sha256: artifacts.x86_64.DEBIAN_SHA256_SERVER?.substring(0, 8) + '…',
            sha256Full: artifacts.x86_64.DEBIAN_SHA256_SERVER,
            url: getDownloadUrl(artifacts.x86_64, artifacts.x86_64.DEBIAN_NAME_SERVER)
          });
        }
        if (artifacts.x86_64?.DEBIAN_NAME_CLIENT) {
          files.push({
            name: artifacts.x86_64.DEBIAN_NAME_CLIENT,
            arch: 'x86-64',
            size: formatFileSize(artifacts.x86_64.DEBIAN_SIZE_CLIENT),
            sha256: artifacts.x86_64.DEBIAN_SHA256_CLIENT?.substring(0, 8) + '…',
            sha256Full: artifacts.x86_64.DEBIAN_SHA256_CLIENT,
            url: getDownloadUrl(artifacts.x86_64, artifacts.x86_64.DEBIAN_NAME_CLIENT)
          });
        }
        if (artifacts.x86_64?.DEBIAN_NAME_DEBUG_SYMBOLS) {
          files.push({
            name: artifacts.x86_64.DEBIAN_NAME_DEBUG_SYMBOLS,
            arch: 'x86-64',
            size: formatFileSize(artifacts.x86_64.DEBIAN_SIZE_DEBUG_SYMBOLS),
            sha256: artifacts.x86_64.DEBIAN_SHA256_DEBUG_SYMBOLS?.substring(0, 8) + '…',
            sha256Full: artifacts.x86_64.DEBIAN_SHA256_DEBUG_SYMBOLS,
            url: getDownloadUrl(artifacts.x86_64, artifacts.x86_64.DEBIAN_NAME_DEBUG_SYMBOLS)
          });
        }
        
        // Add ARM64 files
        if (artifacts.arm64?.DEBIAN_NAME_SERVER) {
          files.push({
            name: artifacts.arm64.DEBIAN_NAME_SERVER,
            arch: 'ARM64',
            size: formatFileSize(artifacts.arm64.DEBIAN_SIZE_SERVER),
            sha256: artifacts.arm64.DEBIAN_SHA256_SERVER?.substring(0, 8) + '…',
            sha256Full: artifacts.arm64.DEBIAN_SHA256_SERVER,
            url: getDownloadUrl(artifacts.arm64, artifacts.arm64.DEBIAN_NAME_SERVER)
          });
        }
        if (artifacts.arm64?.DEBIAN_NAME_CLIENT) {
          files.push({
            name: artifacts.arm64.DEBIAN_NAME_CLIENT,
            arch: 'ARM64',
            size: formatFileSize(artifacts.arm64.DEBIAN_SIZE_CLIENT),
            sha256: artifacts.arm64.DEBIAN_SHA256_CLIENT?.substring(0, 8) + '…',
            sha256Full: artifacts.arm64.DEBIAN_SHA256_CLIENT,
            url: getDownloadUrl(artifacts.arm64, artifacts.arm64.DEBIAN_NAME_CLIENT)
          });
        }
        if (artifacts.arm64?.DEBIAN_NAME_DEBUG_SYMBOLS) {
          files.push({
            name: artifacts.arm64.DEBIAN_NAME_DEBUG_SYMBOLS,
            arch: 'ARM64',
            size: formatFileSize(artifacts.arm64.DEBIAN_SIZE_DEBUG_SYMBOLS),
            sha256: artifacts.arm64.DEBIAN_SHA256_DEBUG_SYMBOLS?.substring(0, 8) + '…',
            sha256Full: artifacts.arm64.DEBIAN_SHA256_DEBUG_SYMBOLS,
            url: getDownloadUrl(artifacts.arm64, artifacts.arm64.DEBIAN_NAME_DEBUG_SYMBOLS)
          });
        }
        
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-xl font-semibold mb-2">{systemRequirements?.distributions?.ubuntu?.name || 'Ubuntu / Debian'}</h4>
            <p className="text-gray-600 mb-4">
              <strong>Supported versions:</strong> {systemRequirements?.distributions?.ubuntu?.supported_versions || '20.04 – 24.04'}<br />
              <strong>Architectures:</strong> {systemRequirements?.distributions?.ubuntu?.architectures || 'x86-64, ARM64'}
            </p>

              {/* Deprecated Version Warning */}
              {versions.find(v => v.value === selectedVersion)?.status === 'deprecated' && (
                <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-4 w-4 text-yellow-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-2">
                      <p className="text-sm text-yellow-800 font-medium">
                        This version is deprecated. Consider upgrading to the latest version.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h6 className="font-medium mb-3">Direct downloads</h6>
                <DownloadTable files={files} />
              </div>

              <h5 className="text-lg font-medium mb-3">Install via APT</h5>
              <CodeBlock id="ubuntu-apt">
{`# Add ArangoDB repository
curl -fsSL https://download.arangodb.com/arangodb.key | sudo gpg --dearmor -o /usr/share/keyrings/arangodb.gpg
echo "deb [signed-by=/usr/share/keyrings/arangodb.gpg] https://download.arangodb.com/DEBIAN/ /" | sudo tee /etc/apt/sources.list.d/arangodb.list

sudo apt update
sudo apt install arangodb3e`}
              </CodeBlock>

              <div className="mt-6">
                <p className="text-sm text-gray-600 mb-2 pt-2">Optional pin to exact version</p>
                <CodeBlock id="ubuntu-version">
{`sudo apt install arangodb3e=${artifacts.x86_64?.DEBIAN_VERSION || selectedVersion}`}
                </CodeBlock>
              </div>

              <div className="mt-4">
                <h6 className="font-medium mb-2">Extras</h6>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  <li>Client tools: <code className="bg-gray-100 px-1 rounded">sudo apt install arangodb3e-client</code></li>
                  <li>Debug symbols: <code className="bg-gray-100 px-1 rounded">sudo apt install arangodb3e-dbg</code></li>
                </ul>
              </div>

              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Verify checksum</p>
                <CodeBlock id="ubuntu-checksum">
{`sha256sum ${artifacts.x86_64?.DEBIAN_NAME_SERVER || 'package.deb'}`}
                </CodeBlock>
              </div>

              <div className="mt-6 p-4 bg-[#BEFE99] rounded-lg">
                <h6 className="font-medium mb-2">After install</h6>
                <ul className="text-sm space-y-1">
                  <li>Web UI: <a href="http://localhost:8529" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">http://localhost:8529</a></li>
                  <li>Default user: <code className="bg-gray-100 px-1 rounded">root</code></li>
                  <li>Start service: <code className="bg-gray-100 px-1 rounded">sudo systemctl enable --now arangodb3e</code></li>
                </ul>
              </div>

              {/* Method Notes Card */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                <h6 className="font-medium mb-2 text-gray-800">Installation Notes</h6>
                <MethodNotes method="linux" />
              </div>
            </div>
          </div>
        );
      })()}

      {selectedDistro === 'redhat' && (() => {
        const artifacts = getArtifactData('rhel');
        if (!artifacts) return <div>Loading...</div>;
        
        const files = [];
        
        // Add x86-64 files
        if (artifacts.x86_64?.RPM_NAME_SERVER) {
          files.push({
            name: artifacts.x86_64.RPM_NAME_SERVER,
            arch: 'x86-64',
            size: formatFileSize(artifacts.x86_64.RPM_SIZE_SERVER),
            sha256: artifacts.x86_64.RPM_SHA256_SERVER?.substring(0, 8) + '…',
            sha256Full: artifacts.x86_64.RPM_SHA256_SERVER,
            url: getDownloadUrl(artifacts.x86_64, artifacts.x86_64.RPM_NAME_SERVER)
          });
        }
        if (artifacts.x86_64?.RPM_NAME_CLIENT) {
          files.push({
            name: artifacts.x86_64.RPM_NAME_CLIENT,
            arch: 'x86-64',
            size: formatFileSize(artifacts.x86_64.RPM_SIZE_CLIENT),
            sha256: artifacts.x86_64.RPM_SHA256_CLIENT?.substring(0, 8) + '…',
            sha256Full: artifacts.x86_64.RPM_SHA256_CLIENT,
            url: getDownloadUrl(artifacts.x86_64, artifacts.x86_64.RPM_NAME_CLIENT)
          });
        }
        if (artifacts.x86_64?.RPM_NAME_DEBUG_SYMBOLS) {
          files.push({
            name: artifacts.x86_64.RPM_NAME_DEBUG_SYMBOLS,
            arch: 'x86-64',
            size: formatFileSize(artifacts.x86_64.RPM_SIZE_DEBUG_SYMBOLS),
            sha256: artifacts.x86_64.RPM_SHA256_DEBUG_SYMBOLS?.substring(0, 8) + '…',
            sha256Full: artifacts.x86_64.RPM_SHA256_DEBUG_SYMBOLS,
            url: getDownloadUrl(artifacts.x86_64, artifacts.x86_64.RPM_NAME_DEBUG_SYMBOLS)
          });
        }
        
        // Add ARM64 files
        if (artifacts.arm64?.RPM_NAME_SERVER) {
          files.push({
            name: artifacts.arm64.RPM_NAME_SERVER,
            arch: 'ARM64',
            size: formatFileSize(artifacts.arm64.RPM_SIZE_SERVER),
            sha256: artifacts.arm64.RPM_SHA256_SERVER?.substring(0, 8) + '…',
            sha256Full: artifacts.arm64.RPM_SHA256_SERVER,
            url: getDownloadUrl(artifacts.arm64, artifacts.arm64.RPM_NAME_SERVER)
          });
        }
        if (artifacts.arm64?.RPM_NAME_CLIENT) {
          files.push({
            name: artifacts.arm64.RPM_NAME_CLIENT,
            arch: 'ARM64',
            size: formatFileSize(artifacts.arm64.RPM_SIZE_CLIENT),
            sha256: artifacts.arm64.RPM_SHA256_CLIENT?.substring(0, 8) + '…',
            sha256Full: artifacts.arm64.RPM_SHA256_CLIENT,
            url: getDownloadUrl(artifacts.arm64, artifacts.arm64.RPM_NAME_CLIENT)
          });
        }
        if (artifacts.arm64?.RPM_NAME_DEBUG_SYMBOLS) {
          files.push({
            name: artifacts.arm64.RPM_NAME_DEBUG_SYMBOLS,
            arch: 'ARM64',
            size: formatFileSize(artifacts.arm64.RPM_SIZE_DEBUG_SYMBOLS),
            sha256: artifacts.arm64.RPM_SHA256_DEBUG_SYMBOLS?.substring(0, 8) + '…',
            sha256Full: artifacts.arm64.RPM_SHA256_DEBUG_SYMBOLS,
            url: getDownloadUrl(artifacts.arm64, artifacts.arm64.RPM_NAME_DEBUG_SYMBOLS)
          });
        }
        
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-xl font-semibold mb-2">{systemRequirements?.distributions?.redhat?.name || 'RedHat / CentOS / Fedora'}</h4>
              <p className="text-gray-600 mb-4">
                <strong>Supported versions:</strong> {systemRequirements?.distributions?.redhat?.supported_versions || 'RHEL 8–9 / CentOS 7–9 / Fedora ≥ 36'}<br />
                <strong>Architecture:</strong> {systemRequirements?.distributions?.redhat?.architectures || 'x86-64, ARM64'}
              </p>

              {/* Deprecated Version Warning */}
              {versions.find(v => v.value === selectedVersion)?.status === 'deprecated' && (
                <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-4 w-4 text-yellow-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-2">
                      <p className="text-sm text-yellow-800 font-medium">
                        This version is deprecated. Consider upgrading to the latest version.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h6 className="font-medium mb-3">Direct downloads</h6>
                <DownloadTable files={files} />
              </div>

              <h5 className="text-lg font-medium mb-3">Install via YUM / DNF</h5>
              <CodeBlock id="redhat-yum">
{`sudo tee /etc/yum.repos.d/arangodb.repo >/dev/null <<'EOF'
[arangodb]
name=ArangoDB
baseurl=https://download.arangodb.com/RPM/
enabled=1
gpgcheck=1
gpgkey=https://download.arangodb.com/arangodb.key
EOF

sudo yum -y install arangodb3e
# or on Fedora:
# sudo dnf -y install arangodb3e`}
              </CodeBlock>

              <p className="text-sm text-gray-600 mb-2 pt-2">Optional pin</p>
              <CodeBlock id="redhat-version">
{`sudo yum -y install arangodb3e-${selectedVersion}`}
              </CodeBlock>

              <div className="mt-4">
                <h6 className="font-medium mb-2">Extras</h6>
                <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                  <li>Client tools: <code className="bg-gray-100 px-1 rounded">sudo yum -y install arangodb3e-client</code></li>
                  <li>Debug symbols: <code className="bg-gray-100 px-1 rounded">sudo yum -y install arangodb3e-debuginfo</code></li>
                </ul>
              </div>

              <div className="mt-6 p-4 bg-[#BEFE99] rounded-lg">
                <h6 className="font-medium mb-2">After install</h6>
                <ul className="text-sm space-y-1">
                  <li>Start service: <code className="bg-gray-100 px-1 rounded">sudo systemctl enable --now arangodb3e</code></li>
                  <li>Web UI: <a href="http://localhost:8529" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">http://localhost:8529</a></li>
                </ul>
              </div>

              {/* Method Notes Card */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                <h6 className="font-medium mb-2 text-gray-800">Installation Notes</h6>
                <MethodNotes method="linux" />
              </div>
            </div>
          </div>
        );
      })()}

      {selectedDistro === 'suse' && (() => {
        const artifacts = getArtifactData('suse');
        if (!artifacts) return <div>Loading...</div>;
        
        const files = [];
        
        // Add x86-64 files
        if (artifacts.x86_64?.RPM_NAME_SERVER) {
          files.push({
            name: artifacts.x86_64.RPM_NAME_SERVER,
            arch: 'x86-64',
            size: formatFileSize(artifacts.x86_64.RPM_SIZE_SERVER),
            sha256: artifacts.x86_64.RPM_SHA256_SERVER?.substring(0, 8) + '…',
            sha256Full: artifacts.x86_64.RPM_SHA256_SERVER,
            url: getDownloadUrl(artifacts.x86_64, artifacts.x86_64.RPM_NAME_SERVER)
          });
        }
        if (artifacts.x86_64?.RPM_NAME_CLIENT) {
          files.push({
            name: artifacts.x86_64.RPM_NAME_CLIENT,
            arch: 'x86-64',
            size: formatFileSize(artifacts.x86_64.RPM_SIZE_CLIENT),
            sha256: artifacts.x86_64.RPM_SHA256_CLIENT?.substring(0, 8) + '…',
            sha256Full: artifacts.x86_64.RPM_SHA256_CLIENT,
            url: getDownloadUrl(artifacts.x86_64, artifacts.x86_64.RPM_NAME_CLIENT)
          });
        }
        if (artifacts.x86_64?.RPM_NAME_DEBUG_SYMBOLS) {
          files.push({
            name: artifacts.x86_64.RPM_NAME_DEBUG_SYMBOLS,
            arch: 'x86-64',
            size: formatFileSize(artifacts.x86_64.RPM_SIZE_DEBUG_SYMBOLS),
            sha256: artifacts.x86_64.RPM_SHA256_DEBUG_SYMBOLS?.substring(0, 8) + '…',
            sha256Full: artifacts.x86_64.RPM_SHA256_DEBUG_SYMBOLS,
            url: getDownloadUrl(artifacts.x86_64, artifacts.x86_64.RPM_NAME_DEBUG_SYMBOLS)
          });
        }
        
        // Add ARM64 files
        if (artifacts.arm64?.RPM_NAME_SERVER) {
          files.push({
            name: artifacts.arm64.RPM_NAME_SERVER,
            arch: 'ARM64',
            size: formatFileSize(artifacts.arm64.RPM_SIZE_SERVER),
            sha256: artifacts.arm64.RPM_SHA256_SERVER?.substring(0, 8) + '…',
            sha256Full: artifacts.arm64.RPM_SHA256_SERVER,
            url: getDownloadUrl(artifacts.arm64, artifacts.arm64.RPM_NAME_SERVER)
          });
        }
        if (artifacts.arm64?.RPM_NAME_CLIENT) {
          files.push({
            name: artifacts.arm64.RPM_NAME_CLIENT,
            arch: 'ARM64',
            size: formatFileSize(artifacts.arm64.RPM_SIZE_CLIENT),
            sha256: artifacts.arm64.RPM_SHA256_CLIENT?.substring(0, 8) + '…',
            sha256Full: artifacts.arm64.RPM_SHA256_CLIENT,
            url: getDownloadUrl(artifacts.arm64, artifacts.arm64.RPM_NAME_CLIENT)
          });
        }
        if (artifacts.arm64?.RPM_NAME_DEBUG_SYMBOLS) {
          console.log('Adding ARM64 Debug Symbols:', artifacts.arm64.RPM_NAME_DEBUG_SYMBOLS);
          files.push({
            name: artifacts.arm64.RPM_NAME_DEBUG_SYMBOLS,
            arch: 'ARM64',
            size: formatFileSize(artifacts.arm64.RPM_SIZE_DEBUG_SYMBOLS),
            sha256: artifacts.arm64.RPM_SHA256_DEBUG_SYMBOLS?.substring(0, 8) + '…',
            sha256Full: artifacts.arm64.RPM_SHA256_DEBUG_SYMBOLS,
            url: getDownloadUrl(artifacts.arm64, artifacts.arm64.RPM_NAME_DEBUG_SYMBOLS)
          });
        } else {
          console.log('ARM64 Debug Symbols not found:', artifacts.arm64);
        }
        
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-xl font-semibold mb-2">{systemRequirements?.distributions?.suse?.name || 'openSUSE / SLE'}</h4>
              <p className="text-gray-600 mb-4">
                <strong>Supported versions:</strong> {systemRequirements?.distributions?.suse?.supported_versions || 'openSUSE 15.x / SLE 15.x'}<br />
                <strong>Architecture:</strong> {systemRequirements?.distributions?.suse?.architectures || 'x86-64, ARM64'}
              </p>

              {/* Deprecated Version Warning */}
              {versions.find(v => v.value === selectedVersion)?.status === 'deprecated' && (
                <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-4 w-4 text-yellow-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-2">
                      <p className="text-sm text-yellow-800 font-medium">
                        This version is deprecated. Consider upgrading to the latest version.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h6 className="font-medium mb-3">Direct downloads</h6>
                <DownloadTable files={files} />
              </div>

              <h5 className="text-lg font-medium mb-3">Install via zypper</h5>
              <CodeBlock id="suse-zypper">
{`sudo zypper ar https://download.arangodb.com/arangodb.suse.repo
sudo zypper ref
sudo zypper in arangodb3`}
              </CodeBlock>

              {/* Method Notes Card */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                <h6 className="font-medium mb-2 text-gray-800">Installation Notes</h6>
                <MethodNotes method="linux" />
              </div>
            </div>
          </div>
        );
      })()}

      {selectedDistro === 'tarball' && (() => {
        const artifacts = getArtifactData('tarball');
        if (!artifacts) return <div>Loading...</div>;
        
        const files = [];
        
        // Add x86-64 files
        if (artifacts.x86_64?.TARGZ_NAME_SERVER) {
          files.push({
            name: artifacts.x86_64.TARGZ_NAME_SERVER,
            arch: 'x86-64',
            size: formatFileSize(artifacts.x86_64.TARGZ_SIZE_SERVER),
            sha256: artifacts.x86_64.TARGZ_SHA256_SERVER?.substring(0, 8) + '…',
            sha256Full: artifacts.x86_64.TARGZ_SHA256_SERVER,
            url: getDownloadUrl(artifacts.x86_64, artifacts.x86_64.TARGZ_NAME_SERVER)
          });
        }
        if (artifacts.x86_64?.TARGZ_NAME_CLIENT) {
          files.push({
            name: artifacts.x86_64.TARGZ_NAME_CLIENT,
            arch: 'x86-64',
            size: formatFileSize(artifacts.x86_64.TARGZ_SIZE_CLIENT),
            sha256: artifacts.x86_64.TARGZ_SHA256_CLIENT?.substring(0, 8) + '…',
            sha256Full: artifacts.x86_64.TARGZ_SHA256_CLIENT,
            url: getDownloadUrl(artifacts.x86_64, artifacts.x86_64.TARGZ_NAME_CLIENT)
          });
        }
        
        // Add ARM64 files
        if (artifacts.arm64?.TARGZ_NAME_SERVER) {
          files.push({
            name: artifacts.arm64.TARGZ_NAME_SERVER,
            arch: 'ARM64',
            size: formatFileSize(artifacts.arm64.TARGZ_SIZE_SERVER),
            sha256: artifacts.arm64.TARGZ_SHA256_SERVER?.substring(0, 8) + '…',
            sha256Full: artifacts.arm64.TARGZ_SHA256_SERVER,
            url: getDownloadUrl(artifacts.arm64, artifacts.arm64.TARGZ_NAME_SERVER)
          });
        }
        if (artifacts.arm64?.TARGZ_NAME_CLIENT) {
          files.push({
            name: artifacts.arm64.TARGZ_NAME_CLIENT,
            arch: 'ARM64',
            size: formatFileSize(artifacts.arm64.TARGZ_SIZE_CLIENT),
            sha256: artifacts.arm64.TARGZ_SHA256_CLIENT?.substring(0, 8) + '…',
            sha256Full: artifacts.arm64.TARGZ_SHA256_CLIENT,
            url: getDownloadUrl(artifacts.arm64, artifacts.arm64.TARGZ_NAME_CLIENT)
          });
        }
        
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-xl font-semibold mb-2">{systemRequirements?.distributions?.tarball?.name || 'TAR (manual install)'}</h4>
              <p className="text-gray-600 mb-4">
                <strong>{systemRequirements?.distributions?.tarball?.supported_versions || 'Any Linux, no root required'}</strong>
              </p>

              {/* Deprecated Version Warning */}
              {versions.find(v => v.value === selectedVersion)?.status === 'deprecated' && (
                <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg className="h-4 w-4 text-yellow-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-2">
                      <p className="text-sm text-yellow-800 font-medium">
                        This version is deprecated. Consider upgrading to the latest version.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h6 className="font-medium mb-3">Direct downloads</h6>
                <DownloadTable files={files} />
              </div>

              <CodeBlock id="tarball-install">
{`wget ${getDownloadUrl(artifacts.x86_64, artifacts.x86_64?.TARGZ_NAME_SERVER || 'arangodb3e-linux.tar.gz')}
tar -xzf ${artifacts.x86_64?.TARGZ_NAME_SERVER || 'arangodb3e-linux.tar.gz'}
cd ${artifacts.x86_64?.TARGZ_NAME_SERVER?.replace('.tar.gz', '') || 'arangodb3e-linux'}
./bin/arangod`}
              </CodeBlock>

              <div className="mt-4">
                <p className="text-sm text-gray-600 mb-2">Verify checksum</p>
                <CodeBlock id="tarball-checksum">
{`sha256sum ${artifacts.x86_64?.TARGZ_NAME_SERVER || 'arangodb3e-linux.tar.gz'}`}
                </CodeBlock>
              </div>

              <div className="mt-4">
                <p className="text-sm text-gray-600">
                  <strong>Requirements:</strong> {systemRequirements?.distributions?.tarball?.requirements || 'glibc ≥ 2.31, OpenSSL ≥ 1.1'}
                </p>
              </div>

              {/* Method Notes Card */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                <h6 className="font-medium mb-2 text-gray-800">Installation Notes</h6>
                <MethodNotes method="tarball" />
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );

  const renderDockerTab = () => {
    const artifactData = getArtifactData('docker');
    const dockerImage = artifactData?.DOCKER_IMAGE || `arangodb/enterprise:${selectedVersion}`;
    
    return (
      <div className="space-y-6">
        <div className="pt-4">
          <p className="text-gray-600 mb-4">
            ArangoDB is available as a multi-architecture Docker image. You can run container images on Linux, as well as on macOS and Windows with <strong>Docker Desktop</strong>, for instance.
          </p>

          {/* Deprecated Version Warning */}
          {versions.find(v => v.value === selectedVersion)?.status === 'deprecated' && (
            <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-4 w-4 text-yellow-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-2">
                  <p className="text-sm text-yellow-800 font-medium">
                    This version is deprecated. Consider upgrading to the latest version.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <h6 className="font-medium mb-3">Architecture Requirements</h6>
            <div className="space-y-3">
              <div className="p-3 bg-red-50 rounded border-l-4 border-red-200">
                <p className="text-sm">
                  For the <code className="bg-red-100 px-1 rounded">linux/amd64</code> image variant, processors must support the <strong>x86-64</strong> instruction set, as well as <strong>SSE 4.2</strong> and <strong>AVX</strong> instruction set extensions (e.g., Intel Sandy Bridge or better, AMD Bulldozer or better).
                </p>
              </div>
              <div className="p-3 bg-red-50 rounded border-l-4 border-red-200">
                <p className="text-sm">
                  For the <code className="bg-red-100 px-1 rounded">linux/arm64/v8</code> image variant, processors must be 64-bit ARM chips (<strong>AArch64</strong>). The minimum requirement is <strong>ARMv8</strong> with <strong>Neon</strong> (SIMD extension). A system with Little Endian byte order is required.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h6 className="font-medium mb-2">In order to install the stand-alone version, use:</h6>
            <CodeBlock id="docker-run">
{`docker run -p 8529:8529 -e ARANGO_ROOT_PASSWORD=openSesame ${dockerImage}`}
            </CodeBlock>
          </div>

          <div className="mt-6">
            <p className="text-gray-600">
              See the documentation on <a href="https://hub.docker.com/_/arangodb" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer"><strong>Docker Hub</strong></a> about running ArangoDB in containers and how to persist your data under Docker.
            </p>
          </div>

          {/* Method Notes Card */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
            <h6 className="font-medium mb-2 text-gray-800">Installation Notes</h6>
            <MethodNotes method="docker" />
          </div>
        </div>
      </div>
    );
  };

  const renderKubernetesTab = () => {
    const artifactData = getArtifactData('k8s');
    const dockerImage = artifactData?.DOCKER_IMAGE || `arangodb/enterprise:${selectedVersion}`;
    
    return (
      <div className="space-y-6">
        <div className="pt-4">
          <p className="text-gray-600 mb-4">
            Deploy ArangoDB with Helm 3+ or the official Operator.
          </p>

          {/* Deprecated Version Warning */}
          {versions.find(v => v.value === selectedVersion)?.status === 'deprecated' && (
            <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-4 w-4 text-yellow-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-2">
                  <p className="text-sm text-yellow-800 font-medium">
                    This version is deprecated. Consider upgrading to the latest version.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <h6 className="font-medium mb-2">Helm Installation</h6>
            <CodeBlock id="helm-install">
{`helm repo add arangodb https://arangodb.github.io/helm-charts
helm repo update
helm install my-arango arangodb/arangodb`}
            </CodeBlock>
          </div>

          <div className="mb-6">
            <h6 className="font-medium mb-2">Operator (Advanced)</h6>
            <p className="text-sm text-gray-600 mb-3">
              Use a <code className="bg-gray-100 px-1 rounded">ArangoDeployment</code> resource:
            </p>
            <CodeBlock id="k8s-operator">
{`apiVersion: database.arangodb.com/v1
kind: ArangoDeployment
metadata:
  name: my-arango
spec:
  image: ${dockerImage}
  license:
    secretName: arango-license-key`}
            </CodeBlock>
          </div>

          {/* Method Notes Card */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
            <h6 className="font-medium mb-2 text-gray-800">Installation Notes</h6>
            <MethodNotes method="kubernetes" />
          </div>

        </div>
      </div>
    );
  };

  const renderNightlyTab = () => {
    return (
      <div className="space-y-6">
        <div className="pt-4">
          <p className="text-gray-600 mb-4">
            Access the latest ArangoDB nightly builds for testing and development. These builds contain cutting-edge features and bug fixes but are not recommended for production use.
          </p>

          {/* Nightly Builds Warning */}
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Warning: Nightly Builds
                </h3>
                <p className="text-sm text-red-700 mt-1">
                  These builds are unstable and may contain bugs. Do not use in production environments.
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h6 className="font-medium mb-2">Docker Containers</h6>
            <p className="text-sm text-gray-600 mb-3">
              Nightly builds are available as Docker containers from our private repository:
            </p>
            <CodeBlock id="nightly-docker-devel">
{`# Latest development nightly
docker pull arangodb/arangodb-preview:devel-nightly
docker run -d -e ARANGO_ROOT_PASSWORD="test" -p 8529:8529 arangodb/arangodb-preview:devel-nightly`}
            </CodeBlock>
          </div>

          <div className="mb-6">
            <h6 className="font-medium mb-2">Specific Release Branches</h6>
            <p className="text-sm text-gray-600 mb-3">
              You can also get nightly builds for specific release branches:
            </p>
            <CodeBlock id="nightly-docker-3-11">
{`# 3.11 series nightly
docker pull arangodb/arangodb-preview:3.11-nightly
docker run -d -e ARANGO_ROOT_PASSWORD="test" -p 8529:8529 arangodb/arangodb-preview:3.11-nightly`}
            </CodeBlock>
          </div>

          <div className="mb-6">
            <h6 className="font-medium mb-2">Download Archive</h6>
            <p className="text-sm text-gray-600 mb-3">
              For other package formats, visit the <a href="https://download.arangodb.com/" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 underline">download archive</a> and select the "nightly" category.
            </p>
          </div>

          <div className="mb-6">
            <h6 className="font-medium mb-2">Documentation</h6>
            <p className="text-sm text-gray-600 mb-3">
              Nightly documentation is available at the <a href="https://www.arangodb.com/docs/devel/" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 underline">Nightly Documentation</a> page. Check the <a href="https://github.com/arangodb/arangodb/blob/devel/CHANGELOG" target="_blank" rel="noopener noreferrer" className="text-green-600 hover:text-green-700 underline">CHANGELOG</a> for what's new.
            </p>
          </div>

        </div>
      </div>
    );
  };

  // Show loading state
  console.log('Component render - loading:', loading, 'releasesData:', releasesData, 'error:', error);
  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading ArangoDB releases...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-red-800 mb-2">Error Loading Releases</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchReleasesData}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const versions = getVersions();

  return (
    <div className="min-h-screen bg-[#F4FAF4]">
      <div className="max-w-6xl mx-auto px-4 py-8">

      {/* Installation Method Tabs with Version Selector */}
      <div className="mb-8">
        <div className="bg-[#F4FAF4] rounded-lg shadow-sm">
          {/* Header with Version Selector */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Select Installation Method</h2>
              <div className="flex items-center gap-3">
                <label htmlFor="version-select" className="text-sm font-medium text-gray-700">
                  Version:
                </label>
                <div className="relative">
                  <select
                    id="version-select"
                    value={selectedVersion}
                    onChange={(e) => setSelectedVersion(e.target.value)}
                    className="appearance-none min-w-[180px] px-3 py-2 pr-8 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 hover:border-gray-400 cursor-pointer text-sm font-medium font-nit-urbanist"
                  >
                    {versions.map((version) => (
                      <option key={version.value} value={version.value}>
                        {version.label} {version.status === 'deprecated' ? '(Deprecated)' : ''}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Tab Navigation */}
          <div className="pt-4">
            <nav className="flex gap-2 tab-navigation">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`flex items-center gap-2.5 px-8 py-3.5 rounded-t-[20px] font-bold text-base transition-all duration-200 tab-button ${
                    selectedTab === tab.id
                      ? 'bg-[#BEFE99] text-black'
                      : 'bg-white text-black hover:bg-[#E8F5E8]'
                  }`}
                >
                  {tab.id === 'linux' && (
                    <img src="./images/linux.svg" alt="Linux" className="w-5 h-5" />
                  )}
                  {tab.id === 'docker' && (
                    <img src="./images/docker.svg" alt="Docker" className="w-5 h-5" />
                  )}
                  {tab.id === 'kubernetes' && (
                    <img src="./images/kubernetes.svg" alt="Kubernetes" className="w-5 h-5" />
                  )}
                  {tab.id === 'nightly' && (
                    <img src="./images/docker.svg" alt="Docker" className="w-5 h-5" style={{filter: 'brightness(0)'}} />
                  )}
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
          
          {/* Tab Content */}
          <div className="px-6 pb-6 bg-white border border-gray-200 rounded-b-lg">
            {selectedTab === 'linux' && renderLinuxTab()}
            {selectedTab === 'docker' && renderDockerTab()}
            {selectedTab === 'kubernetes' && renderKubernetesTab()}
            {selectedTab === 'nightly' && renderNightlyTab()}
          </div>
        </div>
      </div>

      {/* Collapsible Sections - Only show for Linux packages */}

      </div>
    </div>
  );
};

export default ArangoDownloadPage;

